import { ReactElement, useEffect, useState } from 'react';
import { PathMatch } from '@remix-run/router/utils';
import {
    createRoutesFromElements,
    matchPath,
    Navigate,
    Outlet,
    Route,
    Routes,
    useLocation,
    useSearchParams,
} from 'react-router-dom';
import { OfferAgreement } from '../data-layer/business/types';
import { setStartedFrom } from '../data-layer/meta/actions';
import { PostStatus } from '../data-layer/posts/constants';
import { useDispatch } from '../data-layer/useDispatch';
import { fromUserModel } from '../mappers/users/toUserUI';
import { DashboardLayout as DashboardLayoutUI } from '../ui/dashboard/DashboardLayout';
import { DashboardPage } from '../ui/dashboard/DashboardPage';
import { IconDiscovery } from '../ui/icon/IconDiscovery';
import { IconDiscoveryFilled } from '../ui/icon/IconDiscoveryFilled';
import { IconFeedSettings } from '../ui/icon/IconFeedSettings';
import { IconFeedSettingsFilled } from '../ui/icon/IconFeedSettingsFilled';
import { IconSearch } from '../ui/icon/IconSearch';
import { DataTestId, DataTestIdWrapper } from '../ui/layout/DataTestIdWrapper';
import { Avatar } from '../ui/media/Avatar';
import { CpaAcceptedDialog } from '../ui/users/CpaAcceptedDialog/CpaAcceptedDialog';
import { CpaDeclinedDialog } from '../ui/users/CpaDeclinedDialog/CpaDeclinedDialog';
import { CpaModerationDialog } from '../ui/users/CpaModerationDialog/CpaModerationDialog';
import { CpaTrialDialog } from '../ui/users/CpaTrialDialog/CpaTrialDialog';
import { CpaTrialRegistrationDialog } from '../ui/users/CpaTrialRegistrationDialog/CpaTrialRegistrationDialog';
import { CpaWaitRegistrationDialog } from '../ui/users/CpaWaitRegistrationDialog/CpaWaitRegistrationDialog';
import { CreateCollectionDialog } from '../ui/users/CreateCollectionDialog/CreateCollectionDialog';
import { DirectAuthorizationDialog } from '../ui/users/DirectAuthorizationDialog/DirectAuthorizationDialog';
import { OtherMenuOptionsDialog } from '../ui/users/OtherMenuOptionsDialog/OtherMenuOptionsDialog';
import { LocationContext, LocationContextPayload } from '../utils/LocationContext';
import { hasBaseProfile, isBlogger, isDeclinedAdmin } from '../utils/accessService';
import { EventSource } from '../utils/analyticsService';
import { useIsMobile } from '../utils/getIsMobile';
import { getWindow } from '../utils/getWindow';
import { useLocationListener } from '../utils/history';
import { useMeta } from '../utils/useMeta';
import { useScrollRestoration } from '../utils/useScrollRestoration';
import { AppDiscovery } from './AppDiscovery';
import { AppsQrDialog } from './AppsQrDialog';
import { DIALOG_STATE_KEY, DialogRoute, useOpenDialog } from './ModalRoute';
import { AdvertisingCatalog } from './advertising/AdvertisingCatalog';
import { AdvertisingCatalogProducts } from './advertising/AdvertisingCatalogProducts/AdvertisingCatalogProducts';
import { AdvertisingCatalogShops } from './advertising/AdvertisingCatalogShops/AdvertisingCatalogShops';
import { AdvertisingCatalogStartPage } from './advertising/AdvertisingCatalogStartPage/AdvertisingCatalogStartPage';
import { BusinessCatalog } from './business/BusinessCatalog/BusinessCatalog';
import { BusinessContentPromotion } from './business/BusinessContentPromotion';
import { BusinessDetails } from './business/BusinessDetails';
import { BusinessManagement } from './business/BusinessManagement';
import { BusinessPersistence } from './business/BusinessPersistence';
import { BusinessRegistration } from './business/BusinessRegistration/BusinessRegistration';
import { BusinessStatisticSummary } from './business/BusinessStatisticSummary';
import { FeedManagement } from './business/FeedManagement/FeedManagement';
import { InviteMemberDialog } from './business/InviteMemberDialog';
import { OfferAgreementDialog } from './business/OfferAgreementDialog';
import { PartnerRegistration } from './business/PartnerRegistration/PartnerRegistration';
import { SelectCategoriesDialog } from './business/SelectCategoriesDialog';
import { WithoutRangeDialog } from './business/WithoutRangeDialog';
import { makeBusinessCollectionsTab } from './business/makeBusinessCollectionsTab';
import { makeBusinessPostsTab } from './business/makeBusinessPostsTab';
import { AddPostDialog } from './collection/AddPostDialog';
import { CollectionStatusDialog } from './collection/CollectionStatusDialog';
import { DIALOGS } from './constants';
import { ErrorPage } from './error/Error';
import { Notifications } from './notifications/Notifications';
import { PostDetails } from './post/PostDetails';
import { PostPersistence } from './post/PostPersistence';
import { PostProductsDetails } from './post/PostProductsDetails/PostProductsDetails';
import { PostProductsDialog } from './post/PostProductsDialog';
import { PostStatusDialog } from './post/PostStatusDialog';
import { PostsGrid } from './post/PostsGrid/PostsGrid';
import { ReportDialog } from './post/ReportDialog/ReportDialog';
import { Search } from './search/Search';
import { CollectionDetails } from './users/CollectionDetails/CollectionDetails';
import { CreateCollectionFromPostsDialog } from './users/CreateCollectionFromPostsDialog';
import { DeleteFromCollectionDialog } from './users/DeleteFromCollectionDialog';
import { EditCollectionDialog } from './users/EditCollectionDialog/EditCollectionDialog';
import { PromoteShowInFeedDialog } from './users/PromoteShowInFeedDialog';
import { SaveToCollectionDialog } from './users/SaveToCollectionDialog';
import { UserDetails } from './users/UserDetails';
import { useCpaPromoBalance } from './users/useCpaPromoBalance';
import { useCurrentUser } from './users/useCurrentUser';
import { withBusinessAuthProtection } from './withBusinessAuthProtection';
import type { UserModel } from '../data-layer/user/types';

const buildMenuItems = (currentUser: UserModel | undefined) => {
    const isBloggerProfile = isBlogger(currentUser?.role);
    const accountPrefix = isBloggerProfile ? 'profile' : 'businesses';
    const profileSubtitle = isBloggerProfile ? 'Профиль Автора' : 'Бизнес-профиль';
    const cpaPromoBalance = useCpaPromoBalance();

    return [
        {
            id: 'discovery',
            selectedIcon: (
                <DataTestIdWrapper dataTestId={DataTestId.DISCOVERY_BUTTON}>
                    <IconDiscoveryFilled />
                </DataTestIdWrapper>
            ),
            unselectedIcon: (
                <DataTestIdWrapper dataTestId={DataTestId.DISCOVERY_BUTTON}>
                    <IconDiscovery />
                </DataTestIdWrapper>
            ),
            title: 'Интересное',
            to: '/',
        },
        {
            id: 'search',
            selectedIcon: (
                <DataTestIdWrapper dataTestId={DataTestId.SEARCH}>
                    <IconSearch stroke="currentcolor" />
                </DataTestIdWrapper>
            ),
            unselectedIcon: (
                <DataTestIdWrapper dataTestId={DataTestId.SEARCH}>
                    <IconSearch stroke="none" />
                </DataTestIdWrapper>
            ),
            title: 'Поиск',
            to: `/search?searchType=post&query=${
                getWindow()?.sessionStorage.getItem('lastSearch') ?? ''
            }`,
            end: true,
        },
        ...(hasBaseProfile(currentUser)
            ? [
                  {
                      id: 'profile',
                      selectedIcon: (
                          <DataTestIdWrapper dataTestId={DataTestId.PROFILE}>
                              <Avatar
                                  src={currentUser?.avatarUrl}
                                  title={currentUser?.displayName}
                                  size={24}
                                  border="2px solid var(--color-primary)"
                              />
                          </DataTestIdWrapper>
                      ),
                      unselectedIcon: (
                          <DataTestIdWrapper dataTestId={DataTestId.PROFILE}>
                              <Avatar
                                  src={currentUser?.avatarUrl}
                                  title={currentUser?.displayName}
                                  size={24}
                              />
                          </DataTestIdWrapper>
                      ),
                      title: 'Профиль',
                      to: '/profile',
                  },
              ]
            : []),
        ...(currentUser?.profile
            ? [
                  {
                      id: 'business',
                      selectedIcon: (
                          <Avatar
                              src={currentUser.profile.avatarUrl}
                              title={currentUser.profile.displayName}
                              size={24}
                              border="2px solid var(--color-primary)"
                          />
                      ),
                      unselectedIcon: (
                          <Avatar
                              src={currentUser.profile.avatarUrl}
                              title={currentUser.profile.displayName}
                              size={24}
                          />
                      ),
                      title: currentUser.profile.displayName,
                      subtitle: profileSubtitle,
                      cpaBalance: cpaPromoBalance,
                      to: `/${accountPrefix}/${currentUser.profile.publicId}`,
                      end: true,
                      linkMatchPaths: [
                          `/${accountPrefix}/${currentUser.profile.publicId}`,
                          `/${accountPrefix}/${currentUser.profile.publicId}/moderation`,
                          `/${accountPrefix}/${currentUser.profile.publicId}/collections`,
                      ],
                  },
              ]
            : []),
        ...(currentUser?.profile && !isDeclinedAdmin(currentUser?.role)
            ? [
                  {
                      id: 'management',
                      selectedIcon: (
                          <DataTestIdWrapper dataTestId={DataTestId.FEED_MANAGEMENT}>
                              <IconFeedSettingsFilled />
                          </DataTestIdWrapper>
                      ),
                      unselectedIcon: (
                          <DataTestIdWrapper dataTestId={DataTestId.FEED_MANAGEMENT}>
                              <IconFeedSettings />
                          </DataTestIdWrapper>
                      ),
                      title: 'Управление лентой',
                      to: `/${accountPrefix}/${currentUser.profile.publicId}/management`,
                      end: true,
                      linkMatchPaths: [
                          `/${accountPrefix}/${currentUser.profile.publicId}/management`,
                          `/${accountPrefix}/${currentUser.profile.publicId}/management/statistic`,
                      ],
                  },
              ]
            : []),
    ];
};

// Страницы, которые полностью исключены из отслеживания скролла
const SCROLL_BLACKLIST: string[] = [
    '/profile/:businessId/posts/:postId/*',
    '/profile/:businessId/management',
    '/profile/:businessId/catalog/*',
    '/profile/:businessId/*',
];

// Порядок имеет значение - группировка маршрутов для общего ключа скролла
const SCROLL_GROUPING: Array<[string, (matched: PathMatch | null) => string | undefined]> = [
    ['/businesses/:businessId/posts/:postId/*', (matched) => matched?.pathnameBase ?? undefined],
    ['/businesses/:businessId/management', (matched) => matched?.pathnameBase ?? undefined],
    ['/businesses/:businessId/catalog/*', (matched) => matched?.pathnameBase ?? undefined],
    ['/businesses/:businessId/*', (matched) => matched?.pathnameBase ?? undefined],
];

const HistoryListener = (props: { children: ReactElement }) => {
    useMeta({});
    useLocationListener();
    useScrollRestoration({
        buildLocationKey: (location) => {
            const isBlacklisted = SCROLL_BLACKLIST.some((pattern) => {
                const matched = matchPath(pattern, location.pathname);

                return matched !== null;
            });

            if (isBlacklisted) {
                return null;
            }

            // используем группировку маршрутов для общего ключа
            const key = SCROLL_GROUPING.reduce<string | undefined>(
                (builtKey, [route, buildLocationKey]) => {
                    const matched = matchPath(route, location.pathname);

                    return builtKey || buildLocationKey(matched);
                },
                undefined,
            );

            return key ?? location.pathname;
        },
    });

    const location = useLocation();
    const [locationInfo, setLocationInfo] = useState<LocationContextPayload>({});

    useEffect(() => {
        if (location.pathname !== locationInfo.currentLocation) {
            setLocationInfo({
                currentLocation: location.pathname,
                previousLocation: locationInfo.currentLocation,
            });
        }
    }, [location, locationInfo.currentLocation, setLocationInfo]);

    return (
        <LocationContext.Provider value={locationInfo}>{props.children}</LocationContext.Provider>
    );
};

const BusinessPostsOverviewTab = makeBusinessPostsTab('business-overview', {
    statuses: [PostStatus.PUBLISHED, PostStatus.LIMITED],
});

const BusinessPostsModerationTab = withBusinessAuthProtection(
    makeBusinessPostsTab('business-moderation', {
        statuses: [
            PostStatus.MODERATION,
            PostStatus.DECLINED,
            PostStatus.PUBLISHED,
            PostStatus.LIMITED,
            PostStatus.DRAFT,
        ],
    }),
);

const BusinessCollectionsTab = makeBusinessCollectionsTab();

const NotFound = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return <Navigate to="/" />;
};

const MainRoot = () => (
    <DashboardPage gridFeed backButtonEnabled={false}>
        <PostsGrid eventSource={EventSource.MULTICOLUMN} />
    </DashboardPage>
);

const DashboardLayout = () => {
    const currentUser = useCurrentUser();
    const openDialog = useOpenDialog();
    const [query] = useSearchParams();
    const { state } = useLocation();
    const isMobile = useIsMobile();
    const hideOutlet = !isMobile && state?.[DIALOG_STATE_KEY] === DIALOGS.CREATE_COLLECTION;

    const utmSource = query.get('utm_source') || getWindow()?.sessionStorage.getItem('utm_source');
    const startedFrom =
        query.get('started_from') ||
        getWindow()?.sessionStorage.getItem('started_from') ||
        utmSource;

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setStartedFrom(startedFrom));
        startedFrom && getWindow()?.sessionStorage.setItem('started_from', startedFrom);
    }, [dispatch, startedFrom]);

    useEffect(() => {
        if (
            currentUser?.profile?.offerAgreement &&
            currentUser.profile.offerAgreement === OfferAgreement.UNSET &&
            currentUser.profile.id
        ) {
            openDialog(DIALOGS.OFFER_AGREEMENT, { businessId: currentUser?.profile?.id });
        }
    }, [currentUser?.profile?.offerAgreement, currentUser?.profile?.id, openDialog]);

    const cpaPromoBalance = useCpaPromoBalance();

    return (
        <DashboardLayoutUI
            menuItems={buildMenuItems(currentUser)}
            onAppDownloadClick={() => openDialog(DIALOGS.APPS_QR)}
            user={currentUser ? fromUserModel(currentUser) : undefined}
            cpaBalance={cpaPromoBalance}
        >
            {hideOutlet ? null : <Outlet />}
            <DialogRoute name={DIALOGS.EDIT_COLLECTION} element={<EditCollectionDialog />} />
            <DialogRoute
                name={DIALOGS.SAVE_TO_COLLECTION_DIALOG_NAME}
                element={<SaveToCollectionDialog />}
            />
            <DialogRoute
                name={DIALOGS.PROMOTE_SHOW_IN_FEED_COLLECTION_DIALOG_NAME}
                element={<PromoteShowInFeedDialog />}
            />
            <DialogRoute
                name={DIALOGS.DELETE_FROM_COLLECTION_DIALOG_NAME}
                element={<DeleteFromCollectionDialog />}
            />
            <DialogRoute name={DIALOGS.CREATE_INVITE} element={<InviteMemberDialog />} />
            <DialogRoute name={DIALOGS.REPORT_DIALOG} element={<ReportDialog />} />
            <DialogRoute name={DIALOGS.OFFER_AGREEMENT} element={<OfferAgreementDialog />} />
            <DialogRoute name={DIALOGS.WITHOUT_RANGE} element={<WithoutRangeDialog />} />
            <DialogRoute name={DIALOGS.SELECT_CATEGORIES} element={<SelectCategoriesDialog />} />
            <DialogRoute name={DIALOGS.POST_PRODUCTS} element={<PostProductsDialog />} />
            <DialogRoute name={DIALOGS.APPS_QR} element={<AppsQrDialog />} />
            <DialogRoute name={DIALOGS.COLLECTION_STATUS} element={<CollectionStatusDialog />} />
            <DialogRoute name={DIALOGS.POST_STATUS} element={<PostStatusDialog />} />
            <DialogRoute name={DIALOGS.ADD_POST_DIALOG} element={<AddPostDialog />} />
            <DialogRoute name={DIALOGS.OTHER_MENU_OPTIONS} element={<OtherMenuOptionsDialog />} />
            <DialogRoute name={DIALOGS.CREATE_COLLECTION} element={<CreateCollectionDialog />} />
            <DialogRoute
                name={DIALOGS.CREATE_COLLECTION_FROM_POSTS}
                element={<CreateCollectionFromPostsDialog />}
            />
            <DialogRoute
                name={DIALOGS.DIRECT_AUTHORIZATION}
                element={<DirectAuthorizationDialog />}
            />
            <DialogRoute name={DIALOGS.CPA_TRIAL} element={<CpaTrialDialog />} />
            <DialogRoute
                name={DIALOGS.CPA_TRIAL_REGISTRATION}
                element={<CpaTrialRegistrationDialog />}
            />
            <DialogRoute name={DIALOGS.CPA_ACCEPTED} element={<CpaAcceptedDialog />} />
            <DialogRoute
                name={DIALOGS.CPA_WAIT_REGISTRATION}
                element={<CpaWaitRegistrationDialog />}
            />
            <DialogRoute name={DIALOGS.CPA_DECLINED} element={<CpaDeclinedDialog />} />
            <DialogRoute name={DIALOGS.CPA_MODERATION} element={<CpaModerationDialog />} />
            <AppDiscovery />
            <Notifications />
        </DashboardLayoutUI>
    );
};

export const getRootRoute = () => (
    <Route
        path="*"
        element={
            <HistoryListener>
                <>
                    <Routes>
                        <Route path="not-found" element="" />
                        <Route path="/" element={<DashboardLayout />}>
                            <Route path="/" element={<MainRoot />} />
                            <Route path="posts/create" element={<PostPersistence />} />
                            <Route path="posts/:postId/edit" element={<PostPersistence />} />
                            <Route
                                path="posts/:postId/products"
                                element={<PostProductsDetails />}
                            />
                            <Route
                                path="businesses/:businessId/posts/:postId"
                                element={<PostDetails />}
                            />
                            <Route
                                path="profile/:businessId/posts/:postId"
                                element={<PostDetails />}
                            />
                            <Route path="business/posts/:postId" element={<PostDetails />} />
                            <Route path="profile/posts/:postId" element={<PostDetails />} />
                            <Route
                                path="businesses/:businessId/management"
                                element={<BusinessManagement />}
                            >
                                <Route index element={<FeedManagement />} />
                                <Route path="statistic" element={<BusinessStatisticSummary />} />
                            </Route>
                            <Route
                                path="profile/:businessId/management"
                                element={<BusinessManagement />}
                            >
                                <Route index element={<FeedManagement />} />
                                <Route path="statistic" element={<BusinessStatisticSummary />} />
                            </Route>
                            <Route
                                path="businesses/:businessId/catalog/:catalogId"
                                element={<BusinessCatalog />}
                            />
                            <Route path="/promotion" element={<BusinessContentPromotion />} />
                            <Route
                                path="profile/:businessId/catalog/:catalogId"
                                element={<BusinessCatalog />}
                            />
                            <Route path="businesses/:businessId/*" element={<BusinessDetails />}>
                                <Route index element={<BusinessPostsOverviewTab />} />
                                <Route path="moderation" element={<BusinessPostsModerationTab />} />
                                <Route path="collections" element={<BusinessCollectionsTab />} />
                            </Route>
                            <Route path="profile/:businessId/*" element={<BusinessDetails />}>
                                <Route index element={<BusinessPostsOverviewTab />} />
                                <Route path="moderation" element={<BusinessPostsModerationTab />} />
                                <Route path="collections" element={<BusinessCollectionsTab />} />
                            </Route>
                            <Route
                                path="businesses/edit/:businessId"
                                element={<BusinessPersistence />}
                            />
                            <Route
                                path="profile/edit/:businessId"
                                element={<BusinessPersistence />}
                            />
                            <Route
                                path="businesses/registration"
                                element={<BusinessRegistration />}
                            />
                            <Route path="profile/registration" element={<BusinessRegistration />} />
                            <Route path="search" element={<Search />} />
                            <Route path="profile" element={<UserDetails />} />
                            <Route
                                path="collections/:collectionId"
                                element={<CollectionDetails />}
                            />
                            <Route path="partner/registration" element={<PartnerRegistration />} />
                            <Route path="advertising/catalog" element={<AdvertisingCatalog />}>
                                <Route index element={<AdvertisingCatalogStartPage />} />
                                <Route path="products" element={<AdvertisingCatalogProducts />} />
                                <Route path="shops" element={<AdvertisingCatalogShops />} />
                            </Route>
                            <Route path="error/451" element={<ErrorPage />} />
                        </Route>
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </>
            </HistoryListener>
        }
    />
);

export const routes = createRoutesFromElements(getRootRoute());
